import { segmentHealth, walkthroughHealth, type SegmentHealthInput } from './segmentHealth';
import { RECORDING_STALL_AFTER_MS, type RecordingProgressStage } from './recordingProgress';

/**
 * One arc for a walkthrough, on the review page: uploading, processing, ready
 * (TOADO-304).
 *
 * **There are two derivations of this arc, and they are not duplicates.**
 * `recordingProgress.ts` derives it from the local upload record, which is
 * what the recorder page has in the seconds after a stop and what knows how
 * many bytes have actually gone up from this machine. This one derives it
 * from the server's session, which is what the review page has - and has to
 * have, because the review page is reachable from another machine, after a
 * reload, or by a colleague, none of which have a local record at all. The
 * stage names are shared so the two cannot describe the same walkthrough in
 * different words.
 *
 * The page had the information and not the answer. Every status was on screen
 * somewhere - a spinner per clip, a line per transcript - and the question a
 * person actually has, "how long until I can act on this", was answered
 * nowhere. This derives it once, from fields that already exist, so the page
 * renders a state instead of assembling one inline in JSX.
 *
 * **Processing is not one thing**, and that is what shapes this. The master
 * upload, the clip cutting and the transcription are three pipelines that
 * finish at different times. Transcription is the slowest, retries with
 * backoff up to ten attempts, and is off by default: gating "ready" on it
 * would mean most workspaces never reached ready at all. So the arc is gated
 * on clips, and transcripts land underneath as they arrive.
 *
 * **A dead job must never read as ongoing work.** That is TOADO-277, where a
 * failed pipeline presented as a spinner for hours and the person concluded
 * their tickets were lost. Anything that has failed says so, and a session
 * that has stopped moving with no worker on it is called out rather than
 * animated.
 *
 * Per-segment failures are `segmentHealth`'s, not restated here: that module
 * already owns which failures are survivable and what each one costs, and a
 * second opinion on the same question is how the two drift.
 */

export type ReviewStage =
  /**
   * The four the recorder page also uses: uploading (the video is still going
   * up), processing (clips are being cut), ready (clips are done, transcripts
   * may still be arriving underneath), failed (nothing usable will come of
   * this without somebody intervening).
   */
  | RecordingProgressStage
  /** Tickets exist. Only the review page can see this one. */
  | 'committed'
  /** The walkthrough was thrown away. Likewise. */
  | 'discarded';

export interface ReviewProgress {
  stage: ReviewStage;
  headline: string;
  detail?: string;
  /** 0 to 1 while uploading, absent otherwise. Real, not indeterminate. */
  uploadFraction?: number;
  /** Segments that failed in some way and are worth naming on screen. */
  failedSegments: number;
  /** Segments that will still produce a ticket on commit. */
  committableSegments: number;
  /** Transcripts still arriving. Never blocks `ready`. */
  transcriptsPending: number;
  /**
   * Clips still being cut, out of the ones that can still finish.
   *
   * Reported rather than inferable: a caller outside this module can see what
   * will survive and what has failed, and neither of those is the same as
   * what is in flight. The step list on the in-page panel wants exactly this
   * number, and an earlier version guessed it from `committableSegments`,
   * which counts something else and produced a bar that sat at zero for the
   * whole of the stage it was describing.
   */
  clipsPending: number;
  /**
   * Which part of the work failed, when the stage is `failed`.
   *
   * There are two ways to get there and they are not the same thing: the
   * video never arrived, or it arrived and every clip cut from it failed. The
   * headline says which, in prose. A caller drawing the work as steps needs
   * to know which step to mark, and inferring it from the absence of other
   * fields was guesswork that got it wrong.
   */
  failedAt?: 'upload' | 'clips';
  /**
   * Moving, as far as anything here can tell.
   *
   * False for a session that is nominally processing while no worker holds it
   * and nothing has changed for a while. The page says so rather than
   * spinning: a person watching a dead job is the failure this exists for.
   */
  moving: boolean;
}

/** Only what this reads, named, rather than a whole session type. */
export interface ReviewProgressInput {
  status: string;
  masterUploadStatus?: 'pending' | 'uploading' | 'ready' | 'failed';
  masterUploadOffset?: number;
  masterUploadTotalBytes?: number;
  transcriptionAvailable?: boolean;
  /** Last server-side change, for telling stuck work from ongoing work. */
  updatedAt?: string;
  /** A worker is actually running this, from the lease it heartbeats. */
  workerActive?: boolean;
  segments?: SegmentHealthInput[];
}

/**
 * How long a processing session may sit unchanged before it is called out.
 *
 * The recorder page's own threshold, so a walkthrough is not "stuck" in one
 * place and "working" in the other. Generous, because cutting clips for a
 * long walkthrough legitimately takes minutes and a false alarm teaches
 * people to ignore the real one.
 */
export const STALLED_AFTER_MS = RECORDING_STALL_AFTER_MS;

export function reviewProgress(
  session: ReviewProgressInput,
  now: number = Date.now(),
): ReviewProgress {
  const transcriptionAvailable = session.transcriptionAvailable !== false;
  const segments = (session.segments ?? []).filter((segment) => !segment.droppedAt);
  const health = walkthroughHealth(segments, transcriptionAvailable);
  const transcriptsPending = transcriptionAvailable
    ? segments.filter((segment) => segment.transcriptStatus === 'pending').length
    : 0;
  const clipsPending = segments.filter((segment) => (
    segmentHealth(segment, transcriptionAvailable).kind === 'working'
    && segment.clipUploadStatus !== 'ready'
  )).length;
  const counts = {
    failedSegments: health.failed,
    committableSegments: health.committable,
    transcriptsPending,
    clipsPending,
  };

  if (session.status === 'committed') {
    return { stage: 'committed', headline: 'Tickets created', ...counts, moving: false };
  }
  if (session.status === 'discarded') {
    return { stage: 'discarded', headline: 'Walkthrough discarded', ...counts, moving: false };
  }

  if (session.masterUploadStatus === 'failed') {
    return {
      stage: 'failed',
      failedAt: 'upload',
      headline: 'The recording could not be uploaded',
      detail: 'Retry the upload, or discard this walkthrough.',
      ...counts,
      moving: false,
    };
  }

  // Every moment lost its clip. Calling this "processing" is the lie
  // TOADO-277 was about: nothing further is coming.
  if (health.totalLoss) {
    return {
      stage: 'failed',
      failedAt: 'clips',
      headline: 'No part of this walkthrough could be processed',
      detail: 'Every clip failed. Retry the moments below, or discard it.',
      ...counts,
      moving: false,
    };
  }

  // The worker keeps masterUploadStatus uploading while assembling and cutting
  // the acknowledged bytes. That work must not look like an upload at 100%.
  const received = session.status === 'processing'
    && Number.isSafeInteger(session.masterUploadTotalBytes)
    && (session.masterUploadTotalBytes ?? 0) > 0
    && session.masterUploadOffset === session.masterUploadTotalBytes;
  if (!received && (session.masterUploadStatus === 'pending' || session.masterUploadStatus === 'uploading')) {
    const total = session.masterUploadTotalBytes ?? 0;
    const sent = session.masterUploadOffset ?? 0;
    // An upload only moves while the browser holding the recording is open.
    // A tab closed halfway through leaves this exactly as it was, and an
    // animated bar over it is the same lie as a spinner over a dead job: the
    // review page cannot resend those bytes and should not imply it is
    // trying. This is the stage where abandonment is most likely, and it was
    // the one stage with no staleness check at all.
    const moving = isMoving(session, now);
    return {
      stage: 'uploading',
      headline: moving ? 'Uploading the recording' : 'This upload has stopped',
      detail: moving
        ? undefined
        : 'Reopen Toado in the browser that recorded this to finish sending it, or discard it.',
      ...counts,
      // Real progress rather than a spinner: this is the one stage whose
      // remaining time is knowable, and usually the longest.
      ...(total > 0 ? { uploadFraction: Math.min(1, Math.max(0, sent / total)) } : {}),
      moving,
    };
  }

  // The same number `counts` carries, named locally for the branch below.
  const clipsWorking = clipsPending;
  if (session.status !== 'ready_for_review' || clipsWorking > 0) {
    const moving = isMoving(session, now);
    return {
      stage: 'processing',
      headline: moving
        ? received && session.workerActive === false ? 'Waiting to prepare your moments' : 'Preparing your moments'
        : 'This walkthrough has stopped progressing',
      detail: moving
        // Out of the ones that can still finish. Counting failed segments in
        // the denominator produced "1 of 3" for a walkthrough with one dead
        // clip and one already done.
        ? (received && session.workerActive === false
          ? 'Upload complete. Waiting for a processing worker. You can keep working.'
          : clipsWorking > 0
          ? `${clipsWorking} of ${segments.length - health.failed} still being prepared`
          : undefined)
        : 'Nothing has changed for a while and no worker is on it. Retry, or discard it.',
      ...counts,
      moving,
    };
  }

  return {
    stage: 'ready',
    headline: 'Ready to create tickets',
    // Transcripts are named only when they are actually coming. With
    // transcription off the arc says nothing about them at all.
    detail: transcriptsPending > 0
      ? `${transcriptsPending} transcript${transcriptsPending === 1 ? '' : 's'} still arriving. You can create tickets now.`
      : undefined,
    ...counts,
    moving: transcriptsPending > 0,
  };
}

/**
 * Is anything actually working on this, or has it stopped?
 *
 * Positive evidence is required to say no, and that asymmetry is the point. A
 * worker heartbeats its lease without touching the session row, so
 * `updatedAt` alone cannot tell a healthy long job from an abandoned one; the
 * lease can, and the detail endpoint sends it. Accusing a healthy walkthrough
 * of being stuck is worse than missing a stuck one, because the first thing
 * it teaches is to ignore the warning.
 */
function isMoving(session: ReviewProgressInput, now: number): boolean {
  if (session.workerActive === true) return true;
  const updatedAt = session.updatedAt ? Date.parse(session.updatedAt) : Number.NaN;
  // No timestamp is not evidence of a stall. An older API omits it.
  if (!Number.isFinite(updatedAt)) return true;
  return now - updatedAt < STALLED_AFTER_MS;
}
